import { useState, useMemo } from "react";
import { opApi, apiError } from "@/lib/api";
import { money } from "@/components/mc/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Search, Pencil } from "lucide-react";

const priceGroupOf = (p) => (p.variant_options || []).find((o) => o.price_driving && (o.values || []).length) || null;
const variantMinOf = (p) => { const g = priceGroupOf(p); if (!g) return null; const ps = (g.values || []).map((v) => Number(v.price) || 0); return ps.length ? Math.min(...ps) : null; };

export function EditOrderDialog({ order, products = [], modGroups = [], editorName = "", onClose, onSaved }) {
  const groupsFor = (p) => (p.modifier_group_ids || []).map((id) => modGroups.find((g) => g.id === id)).filter(Boolean);
  const lineKey = (pid, modSel, variants) => pid + "|" + (modSel || []).map((m) => m.group_id + ":" + m.option_id).sort().join(",") + "|" + (variants || []).map((v) => v.group + ":" + v.value).sort().join(",");

  const initLines = (order.items || []).map((it, idx) => {
    const modSel = (it.modifiers || []).map((m) => ({ group_id: m.group_id, option_id: m.option_id }));
    const variants = (it.variants || []).map((v) => ({ group: v.group, value: v.value }));
    return {
      key: lineKey(it.product_id, modSel, variants) + "#" + idx,
      product_id: it.product_id, name: it.name, price: Number(it.price) || 0, qty: it.qty,
      taxable: it.taxable !== false, category: it.category || null,
      modSel, modifiers: it.modifiers || [], variants, varLabel: (it.variants || []).map((v) => v.value).join(", "),
    };
  });

  const [lines, setLines] = useState(initLines);
  const [guestName, setGuestName] = useState(order.customer_name || "");
  const [notes, setNotes] = useState(order.notes || "");
  const [fulfillment, setFulfillment] = useState(order.fulfillment || "pickup_now");
  const [scheduledAt, setScheduledAt] = useState(order.scheduled_at ? String(order.scheduled_at).slice(0, 16) : "");
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status || "pay_in_person");
  const [paymentMethod, setPaymentMethod] = useState(order.payment_method || "cash");
  const originalPromo = (order.promo_applied && order.promo_applied.code) || "";
  const [promoCode, setPromoCode] = useState(originalPromo);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  // variant/modifier picker
  const [pickProd, setPickProd] = useState(null);
  const [pickGroups, setPickGroups] = useState([]);
  const [modSel, setModSel] = useState({});
  const [varSel, setVarSel] = useState({});
  const [editingKey, setEditingKey] = useState(null);

  const setQty = (key, d) => setLines((c) => c.map((i) => i.key === key ? { ...i, qty: Math.max(0, i.qty + d) } : i).filter((i) => i.qty > 0));
  const removeLine = (key) => setLines((c) => c.filter((i) => i.key !== key));

  const addPlain = (p) => {
    const base = Number(p.price) || 0;
    const key = lineKey(p.id, [], []) + "#" + Date.now();
    setLines((c) => [...c, { key, product_id: p.id, name: p.name, price: base, qty: 1, taxable: p.taxable !== false, category: p.category || null, modSel: [], modifiers: [], variants: [], varLabel: "" }]);
  };
  const chooseProduct = (p) => {
    const groups = groupsFor(p);
    if (groups.length || (p.variant_options || []).length) { openPicker(p, groups, null); return; }
    addPlain(p); setShowAdd(false);
  };
  const openPicker = (p, groups, existing) => {
    const init = {};
    (groups || groupsFor(p)).forEach((g) => {
      if (existing) { init[g.id] = (existing.modSel || []).filter((m) => m.group_id === g.id).map((m) => m.option_id); }
      else if (g.select_type === "single" && g.required && (g.options || [])[0]) init[g.id] = [g.options[0].id];
      else init[g.id] = [];
    });
    const vinit = {};
    if (existing) { (existing.variants || []).forEach((v) => { vinit[v.group] = v.value; }); }
    setPickProd(p); setPickGroups(groups || groupsFor(p)); setModSel(init); setVarSel(vinit); setEditingKey(existing ? existing.key : null);
  };
  const editLine = (line) => {
    const p = products.find((x) => x.id === line.product_id);
    if (!p || (!groupsFor(p).length && !(p.variant_options || []).length)) { toast.info("This item has no options to edit."); return; }
    openPicker(p, groupsFor(p), line);
  };
  const setVar = (name, val, required) => setVarSel((s) => ({ ...s, [name]: (s[name] === val && !required) ? undefined : val }));
  const toggleMod = (g, oid) => setModSel((s) => {
    const cur = s[g.id] || [];
    if (g.select_type === "single") return { ...s, [g.id]: cur[0] === oid ? (g.required ? cur : []) : [oid] };
    return { ...s, [g.id]: cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid] };
  });
  const pickUnit = useMemo(() => {
    if (!pickProd) return 0;
    const pg = priceGroupOf(pickProd); let base = Number(pickProd.price) || 0;
    if (pg) { const v = (pg.values || []).find((x) => x.name === varSel[pg.name]); base = v ? Number(v.price) || 0 : (variantMinOf(pickProd) ?? base); }
    let d = 0; pickGroups.forEach((g) => (modSel[g.id] || []).forEach((oid) => { const o = (g.options || []).find((x) => x.id === oid); if (o) d += Number(o.price_delta) || 0; }));
    return Math.round((base + d) * 100) / 100;
  }, [pickProd, pickGroups, modSel, varSel]);

  const confirmPick = () => {
    const variants = []; const labels = [];
    for (const vo of (pickProd.variant_options || [])) {
      const chosen = varSel[vo.name];
      if (vo.price_driving && !chosen) { toast.error(`Choose a ${vo.name}.`); return; }
      if (chosen) { variants.push({ group: vo.name, value: chosen }); labels.push(chosen); }
    }
    for (const g of pickGroups) {
      const c = (modSel[g.id] || []).length;
      const mn = g.select_type === "multi" ? (g.min_select || 0) : (g.required ? 1 : 0);
      const mx = g.select_type === "single" ? 1 : (g.max_select || 0);
      if (c < mn) { toast.error(`Choose ${g.select_type === "single" ? "an option" : `at least ${mn}`} for "${g.name}".`); return; }
      if (mx && c > mx) { toast.error(`Choose at most ${mx} for "${g.name}".`); return; }
    }
    const detail = [];
    pickGroups.forEach((g) => (modSel[g.id] || []).forEach((oid) => { const o = (g.options || []).find((x) => x.id === oid); if (o) detail.push({ group_id: g.id, group_name: g.name, option_id: o.id, option_name: o.name, price_delta: Number(o.price_delta) || 0 }); }));
    const sel = detail.map((d) => ({ group_id: d.group_id, option_id: d.option_id }));
    const dispName = pickProd.name + (labels.length ? ` (${labels.join(", ")})` : "");
    const key = lineKey(pickProd.id, sel, variants) + "#" + Date.now();
    const newLine = { key, product_id: pickProd.id, name: dispName, price: pickUnit, qty: 1, taxable: pickProd.taxable !== false, category: pickProd.category || null, modSel: sel, modifiers: detail, variants, varLabel: labels.join(", ") };
    if (editingKey) {
      const oldQty = (lines.find((l) => l.key === editingKey) || {}).qty || 1;
      setLines((c) => c.map((l) => l.key === editingKey ? { ...newLine, qty: oldQty } : l));
    } else {
      setLines((c) => [...c, newLine]);
    }
    setPickProd(null); setPickGroups([]); setModSel({}); setVarSel({}); setEditingKey(null); setShowAdd(false);
  };

  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const filtered = products.filter((p) => { const q = search.trim().toLowerCase(); return !q || (p.name || "").toLowerCase().includes(q); });

  const save = async () => {
    if (!lines.length) { toast.error("An order needs at least one item."); return; }
    setSaving(true);
    try {
      const payload = {
        items: lines.map((l) => ({ product_id: l.product_id, name: l.name, price: l.price, qty: l.qty, modifiers: l.modSel || [], variants: l.variants || [] })),
        guest_name: guestName,
        notes,
        fulfillment,
        scheduled_at: fulfillment === "pickup_later" ? (scheduledAt || null) : null,
        payment_status: paymentStatus,
        payment_method: paymentMethod,
        editor_name: editorName || undefined,
      };
      if (promoCode.trim() !== originalPromo) payload.promo_code = promoCode.trim();
      await opApi.patch(`/tenant/orders/${order.id}`, payload);
      toast.success(`Order #${order.order_number} updated`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not save changes."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg max-h-[92vh] flex flex-col gap-0" data-testid="edit-order-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display">Edit order #{order.order_number}</DialogTitle>
          <DialogDescription>Change items, options, customer or payment — totals & tax are recalculated on save.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 space-y-4">
          {/* line items */}
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Items</div>
            <div className="border border-border divide-y divide-border">
              {lines.map((l) => (
                <div key={l.key} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`edit-line-${l.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{l.name}</div>
                    {(l.modifiers || []).length > 0 && <div className="text-[11px] text-muted-foreground truncate">{l.modifiers.map((m) => m.option_name).join(", ")}</div>}
                  </div>
                  <button type="button" onClick={() => editLine(l)} className="text-muted-foreground hover:text-primary" title="Edit options" data-testid={`edit-line-opts-${l.product_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setQty(l.key, -1)} className="h-6 w-6 border border-border flex items-center justify-center" data-testid={`edit-qty-dec-${l.product_id}`}><Minus className="h-3 w-3" /></button>
                    <span className="w-6 text-center font-mono">{l.qty}</span>
                    <button type="button" onClick={() => setQty(l.key, 1)} className="h-6 w-6 border border-border flex items-center justify-center" data-testid={`edit-qty-inc-${l.product_id}`}><Plus className="h-3 w-3" /></button>
                  </div>
                  <span className="font-mono w-16 text-right">{money(l.price * l.qty)}</span>
                  <button type="button" onClick={() => removeLine(l.key)} className="text-destructive" title="Remove" data-testid={`edit-remove-${l.product_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {!lines.length && <div className="px-3 py-4 text-xs text-muted-foreground text-center">No items — add one below.</div>}
            </div>
            {!showAdd ? (
              <Button variant="outline" size="sm" className="rounded-none mt-2 gap-1" onClick={() => setShowAdd(true)} data-testid="edit-add-item-btn"><Plus className="h-3.5 w-3.5" /> Add item</Button>
            ) : (
              <div className="mt-2 border border-border p-2">
                <div className="relative mb-2">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input autoFocus className="rounded-none h-8 pl-7 text-sm" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="edit-product-search" />
                </div>
                <div className="max-h-48 overflow-y-auto grid grid-cols-2 gap-1">
                  {filtered.map((p) => (
                    <button key={p.id} type="button" onClick={() => chooseProduct(p)} className="border border-border px-2 py-1.5 text-left text-xs hover:bg-secondary" data-testid={`edit-pick-product-${p.id}`}>
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="font-mono text-muted-foreground">{money(variantMinOf(p) != null ? variantMinOf(p) : (p.price || 0))}{variantMinOf(p) != null ? "+" : ""}</div>
                    </button>
                  ))}
                  {!filtered.length && <div className="col-span-2 text-xs text-muted-foreground text-center py-3">No products match.</div>}
                </div>
                <Button variant="ghost" size="sm" className="rounded-none mt-1 text-xs" onClick={() => { setShowAdd(false); setSearch(""); }}>Done adding</Button>
              </div>
            )}
          </div>

          {/* order-level fields */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Customer name</Label><Input className="rounded-none mt-1" value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="edit-customer-name" /></div>
            <div>
              <Label className="text-xs">Lane / fulfillment</Label>
              <select className="mt-1 h-9 w-full rounded-none border border-input bg-background px-2 text-sm" value={fulfillment} onChange={(e) => setFulfillment(e.target.value)} data-testid="edit-fulfillment">
                <option value="pickup_now">In-person / Now</option>
                <option value="pickup_later">Scheduled / Later</option>
              </select>
            </div>
          </div>
          {fulfillment === "pickup_later" && (
            <div><Label className="text-xs">Scheduled time</Label><Input type="datetime-local" className="rounded-none mt-1" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="edit-scheduled-at" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Payment status</Label>
              <select className="mt-1 h-9 w-full rounded-none border border-input bg-background px-2 text-sm" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} data-testid="edit-payment-status">
                <option value="paid">Paid</option>
                <option value="pay_in_person">Pay in person</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Payment method</Label>
              <select className="mt-1 h-9 w-full rounded-none border border-input bg-background px-2 text-sm" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="edit-payment-method">
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div><Label className="text-xs">Promo code <span className="text-muted-foreground normal-case">(blank to remove)</span></Label><Input className="rounded-none mt-1" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} placeholder="e.g. SAVE10" data-testid="edit-promo-code" /></div>
          <div><Label className="text-xs">Notes</Label><Textarea className="rounded-none mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" /></div>

          {(order.edit_history || []).length > 0 && (
            <div className="text-[11px] text-muted-foreground border-t border-border pt-2" data-testid="edit-history">
              Last edited by {order.edit_history[order.edit_history.length - 1].by} · {new Date(order.edit_history[order.edit_history.length - 1].at).toLocaleString()}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-3 border-t border-border flex-row items-center justify-between">
          <span className="text-sm">Subtotal <b className="font-mono">{money(subtotal)}</b> <span className="text-xs text-muted-foreground">+ tax on save</span></span>
          <Button className="rounded-none" onClick={save} disabled={saving || !lines.length} data-testid="edit-order-save">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>

      {/* variant / modifier picker */}
      <Dialog open={!!pickProd} onOpenChange={(o) => { if (!o) { setPickProd(null); setPickGroups([]); setModSel({}); setVarSel({}); setEditingKey(null); } }}>
        <DialogContent className="rounded-none max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-modifier-dialog">
          {pickProd && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{pickProd.name}</DialogTitle>
                <DialogDescription>Choose options for this item.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {(pickProd.variant_options || []).map((vo) => (
                  <div key={vo.name} data-testid={`edit-var-group-${vo.name}`}>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{vo.name} {vo.price_driving ? "· required" : "· optional"}</div>
                    <div className="space-y-1">
                      {(vo.values || []).map((v) => {
                        const on = varSel[vo.name] === v.name;
                        return (
                          <button key={v.name} type="button" onClick={() => setVar(vo.name, v.name, vo.price_driving)} data-testid={`edit-var-opt-${vo.name}-${v.name}`}
                            className={`w-full flex items-center justify-between px-3 py-2 border text-sm transition-colors ${on ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}>
                            <span className="flex items-center gap-2"><span className={`inline-block h-3.5 w-3.5 border rounded-full ${on ? "bg-primary border-primary" : "border-muted-foreground"}`} />{v.name}</span>
                            {vo.price_driving ? <span className="font-mono text-xs text-muted-foreground">{money(v.price)}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {pickGroups.map((g) => (
                  <div key={g.id} data-testid={`edit-mod-group-${g.id}`}>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                      {g.name} {g.select_type === "single" && g.required ? "· required" : g.select_type === "multi" ? `· pick ${g.min_select || 0}${g.max_select ? `–${g.max_select}` : "+"}` : "· optional"}
                    </div>
                    <div className="space-y-1">
                      {(g.options || []).map((o) => {
                        const on = (modSel[g.id] || []).includes(o.id);
                        return (
                          <button key={o.id} type="button" onClick={() => toggleMod(g, o.id)} data-testid={`edit-mod-opt-${o.id}`}
                            className={`w-full flex items-center justify-between px-3 py-2 border text-sm transition-colors ${on ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"}`}>
                            <span className="flex items-center gap-2"><span className={`inline-block h-3.5 w-3.5 border ${g.select_type === "single" ? "rounded-full" : ""} ${on ? "bg-primary border-primary" : "border-muted-foreground"}`} />{o.name}</span>
                            {Number(o.price_delta) ? <span className="font-mono text-xs text-muted-foreground">+{money(o.price_delta)}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="font-mono font-bold text-lg">{money(pickUnit)}</span>
                <Button className="rounded-none" onClick={confirmPick} data-testid="edit-mod-confirm">{editingKey ? "Update line" : "Add to ticket"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
